// src/api/mutator.ts
//? Transporte único do cliente gerado pelo Orval (`bun run map`): token, aviso do backend e toasts de erro.
//* Sem toast de sucesso automático — cada tela mostra o próprio no `onSuccess` (decisão 14 do Orval).

import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';

import useAuth from 'global/Auth/use';

declare module 'axios' {
  interface AxiosRequestConfig {
    //? true = a tela trata o erro sozinha; o mutator não mostra o toast genérico
    silent?: boolean;
  }
}

//! Sem a URL da API toda chamada iria para o próprio Portal — melhor falhar com uma mensagem clara (como fazia o
//! antigo api/index.ts)
if (!import.meta.env.VITE_API_URL) {
  throw new Error('VITE_API_URL ausente. Crie Portal/.env.local a partir de .env.exemple e defina VITE_API_URL.');
}

export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
});

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  //? Com FormData, o navegador precisa gerar o Content-Type com o boundary
  if (config.data instanceof FormData) delete config.headers['Content-Type'];
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    //? D-10: o backend avisa por cabeçalho quando um cadastro reaproveitou uma conta já existente
    const warning = response.headers?.['x-vila-warning'];
    if (warning) toast.warning(decodeURIComponent(warning));
    return response;
  },
  (error: AxiosError) => {
    if (!error.config?.silent) notifyError(error);
    return Promise.reject(error);
  }
);

//* Ordem: detail → errors (ModelState) → message → error → title → corpo em texto → fallback por status
export function extractErrorMessage(data: unknown, status?: number): string {
  if (typeof data === 'string' && data.trim() !== '') return data.trim();
  if (data && typeof data === 'object') {
    const body = data as Record<string, unknown>;
    if (typeof body.detail === 'string' && body.detail) return body.detail;
    if (body.errors && typeof body.errors === 'object') {
      const messages = Object.values(body.errors as Record<string, unknown>)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .filter((value): value is string => typeof value === 'string' && value !== '');
      if (messages.length > 0) return messages.join('\n');
    }
    for (const key of ['message', 'error', 'title'] as const) {
      if (typeof body[key] === 'string' && body[key]) return body[key] as string;
    }
  }
  return fallbackMessage(status);
}

function fallbackMessage(status?: number): string {
  if (status === 404) return 'Não encontrado.';
  if (status === 409) return 'Conflito com um registro existente.';
  if (status && status >= 500) return 'Erro no servidor.';
  return 'Não foi possível concluir a operação.';
}

const SESSION_EXPIRED_KEY = 'vila:session-expired';
const SESSION_EXPIRED_MESSAGE = 'Sessão expirada. Faça login novamente.';

//* Chamado pelo App ao montar: mostra o aviso de sessão expirada deixado antes do recarregamento
export function showPendingSessionNotice(): void {
  try {
    if (!sessionStorage.getItem(SESSION_EXPIRED_KEY)) return;
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  } catch {
    return;
  }
  toast.error(SESSION_EXPIRED_MESSAGE);
}

function notifyError(error: AxiosError): void {
  if (error.code === AxiosError.ERR_CANCELED) return;

  const status = error.response?.status;
  if (!error.response) {
    toast.error('Erro de rede. Verifique sua conexão.');
    return;
  }

  if (status === 401) {
    expireSession();
    return;
  }

  //? Download/exportação (responseType: 'blob'): a mensagem do backend vem dentro do Blob
  if (error.response.data instanceof Blob) {
    readBlobText(error.response.data)
      .then((text) => {
        let body: unknown = text;
        try {
          body = JSON.parse(text);
        } catch {
          body = text;
        }
        showMessage(status, extractErrorMessage(body, status));
      })
      .catch(() => showMessage(status, fallbackMessage(status)));
    return;
  }

  showMessage(status, extractErrorMessage(error.response.data, status));
}

//? O toast não sobrevive ao recarregamento para /login (testado no navegador: sumia sem aparecer) — fica
//? guardado e o App mostra ao montar (showPendingSessionNotice). Várias 401 juntas viram um aviso só.
function expireSession(): void {
  try {
    sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
  } catch {
    toast.error(SESSION_EXPIRED_MESSAGE);
  }
  useAuth.getState().logout?.();
  localStorage.removeItem('token');
  window.location.href = '/login';
}

//* Mesmo tratamento de erro para chamadas fora do axios (streaming SSE do assistente - hooks/Ai/useAiChatStream):
//* 401 → logout; 4xx/5xx → toast com a mensagem do backend
export function notifyHttpError(status: number, body: unknown): void {
  if (status === 401) {
    expireSession();
    return;
  }
  showMessage(status, extractErrorMessage(body, status));
}

//? FileReader em vez de Blob.text(): funciona também em ambientes sem Blob.text (ex.: testes com jsdom)
function readBlobText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

function showMessage(status: number | undefined, message: string): void {
  if (status === 403) {
    toast.error(`Usuário não tem permissão: ${message}`);
    return;
  }
  toast.error(message);
}

export type ApiRequestOptions = AxiosRequestConfig;

//? Trava de duplo clique central (CLAUDE.md): uma escrita idêntica (mesmo método, URL, parâmetros e corpo) feita
//? enquanto a primeira ainda está em andamento não vai ao servidor — recebe o mesmo resultado da primeira. Assim sai
//? uma requisição só, e o `onSuccess` da última `mutate` (o único que o React Query executa) continua rodando.
//? Testado no navegador: com o `isPending` dos formulários, dois cliques seguidos ainda enviavam dois POST — e, com a
//? API rápida, o segundo clique chegava depois da resposta, durante a animação de fechar a janela. Por isso o
//? resultado de sucesso continua valendo por DUPLICATE_WINDOW_MS depois de concluído; erro libera na hora (nova
//? tentativa vai ao servidor).
const DUPLICATE_WINDOW_MS = 1500;
const inFlight = new Map<string, Promise<unknown>>();

function bodyKey(data: unknown): string {
  if (data instanceof FormData) {
    return JSON.stringify(
      [...data.entries()].map(([name, value]) =>
        value instanceof File ? [name, value.name, value.size, value.lastModified] : [name, String(value)]
      )
    );
  }
  if (data === undefined || data === null) return '';
  return typeof data === 'string' ? data : JSON.stringify(data);
}

function writeKey(config: AxiosRequestConfig): string | null {
  const method = (config.method ?? 'GET').toUpperCase();
  if (method === 'GET' || method === 'HEAD' || method === 'OPTIONS') return null;
  return `${method} ${config.url} ${JSON.stringify(config.params ?? {})} ${bodyKey(config.data)}`;
}

export const apiRequest = async <T>(
  config: AxiosRequestConfig,
  options?: ApiRequestOptions
): Promise<T> => {
  const merged = { ...config, ...options };
  const key = writeKey(merged);
  if (key && inFlight.has(key)) return inFlight.get(key) as Promise<T>;

  const request = axiosInstance.request<T>(merged).then((response) => response.data);
  if (!key) return request;
  inFlight.set(key, request);
  try {
    const data = await request;
    setTimeout(() => {
      if (inFlight.get(key) === request) inFlight.delete(key);
    }, DUPLICATE_WINDOW_MS);
    return data;
  } catch (error) {
    inFlight.delete(key);
    throw error;
  }
};

export type ErrorType<E> = AxiosError<E>;
export type BodyType<B> = B;

export default apiRequest;
